/**
 * Reading the application queue's server-computed clocks.
 *
 * AP-41. Nothing here DERIVES an age — `days_in_state` arrives computed by
 * `backend/src/helpers/applicationTiming.js`, off the record's `audit_trail`.
 * (#2230's per-stage clocks retired 2026-10-26.)
 * This module only turns those numbers into the words on the row, so the two
 * halves can never disagree about how old something is.
 */
import type { FamilyApplication } from '@/types/applications';
import { APPLICATION_STATUS_DISPLAY } from '@/lib/applications-display';
import { trfEventDate } from '@/lib/trfTime';
import { formatPreset } from '@/i18n/intl';

/** "11 days outstanding" — the age of an `offer_sent` row, in the offer's words. */
export function offerOutstandingLabel(days: number | null | undefined): string {
  if (typeof days !== 'number') return 'Sent date unknown';
  if (days === 0) return 'Sent today';
  return days === 1 ? '1 day outstanding' : `${days} days outstanding`;
}

/**
 * "Sent Sep 2, 2026" / "Accepted Sep 9, 2026" — the offer's own dates, when the row has them.
 * "Accepted", not "Signed": until DocuSign (#1043) staff record the family's
 * acceptance by hand and nothing is signed (Victor 2026-10-12).
 */
export function offerDateLabel(kind: 'Sent' | 'Accepted', iso: string | null | undefined): string | null {
  const date = trfEventDate(iso);
  return date ? `${kind} ${date}` : null;
}

/**
 * Statuses at which an application has reached its end. The row names the day
 * it got there ("Admitted Sep 3, 2026") rather than keeping a running count —
 * a decision date is something staff cite; "18 days in state" on a settled row
 * told them nothing they could use (Victor, 2026-09-15).
 */
export const SETTLED_STATUSES: ReadonlySet<string> = new Set([
  'admitted', 'staff_declined', 'withdrawn',
  'offer_declined', 'offer_voided',
  // Legacy strings (#1037; #2235 merged offer_expired into offer_voided).
  'approved', 'denied', 'offer_expired',
]);

/**
 * After this many days a running count stops helping ("Under review for 61
 * days") and the row names the date instead ("Under review since Jul 16,
 * 2026"). Relative phrases are for the recent past; older values are compared
 * and cited, and a date does that (UX Movement / Cloudscape timestamp
 * guidance, research 2026-09-15).
 */
export const RELATIVE_DAYS_LIMIT = 28;

/**
 * "Sep 3, 2026" in UTC, or null when the server sent no usable date. Kept for
 * callers whose values may be calendar days stored as UTC midnight. An
 * application's own event instants (state entered, offer sent or signed) use
 * `trfEventDate`, so the queue and the application page name the same day.
 */
export function shortDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return formatPreset(d, 'monthDayYear', { timeZone: 'UTC' });
}

/**
 * The stage a row is in, in the words a reader would use. Every status label
 * works as-is ("Under review for 18 days") except `submitted`, whose label
 * names the family's act rather than what the row is waiting for.
 */
function stageWords(status: string): string {
  if (status === 'submitted') return 'Waiting for review';
  return (APPLICATION_STATUS_DISPLAY as Record<string, { label: string }>)[status]?.label ?? status;
}

const dayCount = (days: number) => (days === 1 ? '1 day' : `${days} days`);

/**
 * How long the row has been where it is, in plain words that NAME the stage.
 *
 * "18 days in state" (until 2026-09-15) made the reader ask what a state was.
 * The stage is now part of the phrase, the count is relative while it is
 * recent and becomes a date once it is not, and a settled row names its
 * decision date. `null` days is "we cannot tell" — a row with no usable trail
 * and no creation date — and it must not render as a number, which is a claim.
 */
export function timeInStatusLabel(
  app: Pick<FamilyApplication, 'status' | 'days_in_state' | 'state_entered_at'>,
): string {
  const days = app.days_in_state;
  const since = trfEventDate(app.state_entered_at);
  const stage = stageWords(app.status);
  if (typeof days !== 'number') return 'Time in this status unknown';
  if (SETTLED_STATUSES.has(app.status)) {
    if (since) return `${stage} ${since}`;
    return days === 0 ? `${stage} today` : `${stage} ${dayCount(days)} ago`;
  }
  if (days === 0) return `${stage} since today`;
  if (days > RELATIVE_DAYS_LIMIT && since) return `${stage} since ${since}`;
  return `${stage} for ${dayCount(days)}`;
}

/**
 * The exact date behind a relative phrase, for the row's `title` — a reader
 * who needs the day can hover for it without the row spelling both out.
 * Null when the phrase already names the date, or no date is known.
 */
export function timeInStatusTitle(
  app: Pick<FamilyApplication, 'status' | 'days_in_state' | 'state_entered_at'>,
): string | undefined {
  const since = trfEventDate(app.state_entered_at);
  if (!since || typeof app.days_in_state !== 'number') return undefined;
  if (SETTLED_STATUSES.has(app.status) || app.days_in_state > RELATIVE_DAYS_LIMIT) return undefined;
  return `In this status since ${since}`;
}

/**
 * Where the application came from, for every row — including the ones with no
 * code, which is most of them once codes became optional.
 *
 * A code-less application still HAS an origin: how the family reached TRF.
 * Rendering nothing for those rows would answer "where did this come from"
 * for a minority and leave the rest looking like missing data.
 *
 * `origin_code: null` on a row that carries an `application_code_id` means the
 * lookup failed, not that there is no code — those two must not read alike.
 */
export function originLabel(
  app: Pick<FamilyApplication, 'origin_code' | 'application_code_id' | 'referral_method'>,
): string {
  // Every branch says the word "referral": "No source recorded" (until
  // 2026-09-15) left the reader asking "source of what?". The thing recorded is
  // how the family reached TRF, and the platform already calls that a
  // referral (the Referral codes page).
  if (app.origin_code?.code) return `Referral code ${app.origin_code.code}`;
  if (app.application_code_id) return 'Referral code unavailable';
  switch (app.referral_method) {
    case 'code':          return 'Referral code unavailable';
    // 2026-09-13 vocabulary: an invite link is how a one-time referral code
    // reaches a family, so the origin reads as the code, not the envelope.
    case 'invite':        return 'One-time referral code';
    case 'self_attested': return 'Self-referred';
    case 'staff_entered': return 'Referral entered by staff';
    case 'none':
    default:              return 'No referral recorded';
  }
}

/**
 * The "Time in status" cell of the Applications table (2026-09-18, guideline
 * §3.1): the count alone, because the stage is the Status column beside it
 * and a row says each thing once. `text` is what the cell prints — "7 days",
 * "today", "since Jul 16, 2026", or the decision date on a settled row, the
 * date the acceptance was recorded on an accepted one — and `title` is the full phrase the
 * card used to print, so a hover still reads the sentence.
 */
export function timeInStatusCell(
  app: Pick<FamilyApplication, 'status' | 'days_in_state' | 'state_entered_at' | 'offer_sent_at' | 'offer_signed_at'>,
): { text: string; title: string } {
  const phrase = timeInStatusLabel(app);
  const days = app.days_in_state;
  const since = trfEventDate(app.state_entered_at);
  if (app.status === 'offer_signed') {
    const signed = offerDateLabel('Accepted', app.offer_signed_at);
    const sent = offerDateLabel('Sent', app.offer_sent_at);
    return {
      text: signed ? signed.replace(/^Accepted /, '') : '—',
      title: [signed, sent].filter(Boolean).join(' · ') || phrase,
    };
  }
  if (typeof days !== 'number') return { text: '—', title: phrase };
  if (SETTLED_STATUSES.has(app.status)) {
    return { text: since ?? (days === 0 ? 'today' : `${dayCount(days)} ago`), title: phrase };
  }
  if (app.status === 'offer_sent') {
    const sent = offerDateLabel('Sent', app.offer_sent_at);
    return {
      text: days === 0 ? 'today' : dayCount(days),
      title: [offerOutstandingLabel(days), sent].filter(Boolean).join(' · '),
    };
  }
  if (days === 0) return { text: 'today', title: phrase };
  if (days > RELATIVE_DAYS_LIMIT && since) return { text: `since ${since}`, title: phrase };
  const exact = timeInStatusTitle(app);
  return { text: dayCount(days), title: exact ? `${phrase} · ${exact}` : phrase };
}
